import * as genericService from "./genericFormServices";
import { type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";
import type { LegalAdviceFormFourData } from "../types/LegalAdvices/LegalAdviceFormFour.type";

const ENDPOINT = "legal-advice-form-four";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormFourData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form One
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormFourData => {
  if (!item) return {} as LegalAdviceFormFourData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormFour || item.legalAdviceForm || item.formData || {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_FOUR',
    status: item.status || form.status || 'DRAFT',
    officeId: form.officeId || item.officeId || item.originOffice?.id || '',
    officeName: form.officeName || item.officeName || item.originOffice?.name || '',
    legalNo: form.legalNo || item.legalNo || '',
    signatureImage: form.signatureImage || item.signatureImage || '',
    signName: form.signName || item.signName || '',
    signerName: form.signerName || item.signerName || '',
    sinnerOfficeName: form.sinnerOfficeName || item.sinnerOfficeName || '',

    // Form Specific Text Fields
    srNo: form.srNo || '',             // (၁) အမှတ်စဉ်
    lawRegisterNo: form.lawRegisterNo || '',      // (၂) ဥပရ မှတ်ပုံတင် ( ) အမှတ်စဉ်
    dateAndSection: form.dateAndSection || '',     // (၃) စွဲတင်သည့် ရက်စွဲနှင့် ဥပဒေပုဒ်မ
    courtAndCaseNo: form.courtAndCaseNo || '',     // (၄) တရားရုံး အမှုအမှတ်
    stationAndCrNo: form.stationAndCrNo || '',     // (၅) ရဲစခန်းအမည်နှင့် (ပ) အမှတ်
    orderDateAndSummary: form.orderDateAndSummary || '',// (၆) အမိန့်ရသည့် ရက်စွဲနှင့် အမိန့်အကျဉ်း
    advocateOpinion: form.advocateOpinion || '',    // (၇) အမှုလိုက်ဥပဒေအရာရှိ နိဂုံးချုပ်မှတ်ချက်
    officerOpinion: form.officerOpinion || '',     // (၈) တာဝန်ခံဥပဒေအရာရှိ နိဂုံးချုပ်မှတ်ချက်
    advocateName: form.advocateName || '',      // (၉) အမှုလိုက်ဥပဒေအရာရှိ အမည်



    // Geo Levels (FIXED: Added targetGeoLevels and originGeoLevel)
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata (User, Office, Logs)
    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
  ? item.stageHistory.map((history: any) => ({
      id: history.id,
      action: history.action,
      comments: history.comments,
      attachments: history.attachments,
      createdAt: history.createdAt,
      performedBy: history.performedBy
        ? {
            id: history.performedBy.id,
            name: history.performedBy.name,
            role: history.performedBy.role,
            position: history.performedBy.position,
            office: history.performedBy.office
              ? {
                  id: history.performedBy.office.id,
                  name: history.performedBy.office.name,
                  code: history.performedBy.office.code,
                  level: history.performedBy.office.level,
                }
              : undefined,
          }
        : undefined,
    }))
  : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form One
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormFourData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate || Boolean(app.date && app.date.startsWith(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.refNo && app.refNo.toLowerCase().includes(term)) ||
    Boolean(app.complainant && app.complainant.toLowerCase().includes(term)) ||
    Boolean(app.accused && app.accused.toLowerCase().includes(term)) ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions (Office User - Form Endpoint)
// ---------------------------------------------------------------------------

// Fetch All / Filtered Applications
export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormFourData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

// Fetch Single Application by ID
export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormFourData> =>
  genericService.fetchApplicationById<LegalAdviceFormFourData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

// Save Draft (Create POST or Update PUT)
export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormFourData>
) => genericService.saveDraft<LegalAdviceFormFourData>(ENDPOINT, id, data);

// Submit Form (Includes Signatures inside DTO)
export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormFourData
) => genericService.submitForm<LegalAdviceFormFourData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions (Global DocumentController)
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);


// Approve Form (PATCH /documents/:id/approve)
export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id , comments, attachments, targetGeoLevels);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);
// // Return Form for Revisions (PATCH /documents/:id/return)
// export const returnLegalAdviceForm = (id: string, remark?: string) =>
//   genericService.returnForm(id, remark);

// // Reject Form (PATCH /documents/:id/reject)
// export const rejectLegalAdviceForm = (id: string, remark?: string) =>
//   genericService.rejectForm(id, remark);